const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatMetres = (value) =>
  Number(value.toFixed(6)).toString();

const routesFromValue = (value) =>
  (Array.isArray(value) ? value : [value]).filter(
    (route) => route && typeof route === 'object',
  );

const binBounds = (label) => {
  const match = String(label || '').match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
  return match ? [Number(match[1]), Number(match[2])] : [Number.POSITIVE_INFINITY, 0];
};

/**
 * Identidad física de una ruta de winche en un día. El ancho de bin y el piso de validez quedan
 * fuera a propósito: si el contrato cambió en caliente no deben aparecer dos series sumables para
 * el mismo equipo, sino una sola y un aviso de contrato incompatible.
 */
const routeIdentity = (day, route) =>
  [day, route?.winche_id, route?.id_sensor].join('|');

const measuredWindows = (route) =>
  (finiteNumber(route?.fed?.total) || 0) + (finiteNumber(route?.unfed?.total) || 0);

const selectedEntryValues = (entries, selectedDays) =>
  (Array.isArray(entries) ? entries : []).filter(
    (entry) => !selectedDays?.length || selectedDays.includes(entry?.day),
  );

const addBins = (target, source) => {
  Object.entries(source || {}).forEach(([label, count]) => {
    target[label] = (target[label] || 0) + (finiteNumber(count) || 0);
  });
};

const sumCounts = (bins) =>
  Object.values(bins || {}).reduce((total, count) => total + (finiteNumber(count) || 0), 0);

const dayOrder = (left, right) => {
  const [ld, lm, ly] = String(left).split('/').map(Number);
  const [rd, rm, ry] = String(right).split('/').map(Number);
  return new Date(ly, lm - 1, ld) - new Date(ry, rm - 1, rd);
};

/**
 * Una banda con muy poco tiempo alimentando produce tasas inestables: se marca, no se oculta.
 * El criterio es relativo al período a propósito: un piso absoluto en minutos marcaría como dudosa
 * una banda que concentra el 17% de una alimentación de tres horas, que es una muestra legítima.
 */
export const DEPTH_LOW_SAMPLE_PCT = 2;

/** Condiciones mínimas para publicar una conclusión en vez de mostrar el dato como referencia. */
export const DEPTH_CONFIDENCE = {
  minCoveragePct: 25,
  minFactor: 1.3,
};

/**
 * Un hueco ocupa sus metros reales: ver el vacío es lo que impide leer dos bandas distantes como
 * vecinas. Más allá de este tope la columna crecería sin aportar, así que se corta y se rotula
 * cuántos metros hay de verdad.
 */
export const DEPTH_GAP_CAP_M = 6;

/** Acepta una jaula, un arreglo de jaulas, o ambos; descarta repetidas por nombre. */
const asCageList = (jaula, jaulas) => {
  const candidates = [
    ...(jaula ? [jaula] : []),
    ...(Array.isArray(jaulas) ? jaulas : (jaulas ? [jaulas] : [])),
  ].filter(Boolean);
  const seen = new Set();
  return candidates.filter((cage) => {
    const key = cage?.name ?? cage;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Jaulas que tienen algo que mostrar en la vista de profundidad: ventanas del acumulador, o
 * alarmas del período —incluso sin profundidad conocida, porque esas se informan aparte—.
 * Es la misma condición que decide el estado vacío del diálogo, así que el botón que lo abre puede
 * consultarla y ambos no pueden discrepar.
 */
export const listDepthCages = (input) => {
  const cages = Array.isArray(input) ? input.filter(Boolean) : (input ? [input] : []);
  return cages.filter(
    (cage) => cage?.depth_analytics?.length
      || cage?.alarms_data?.some((entry) => Array.isArray(entry?.value) && entry.value.length),
  );
};

export const listDepthDays = (input) => {
  const cages = Array.isArray(input) ? input.filter(Boolean) : (input ? [input] : []);
  return Array.from(
    new Set(
      cages.flatMap((cage) => [
        ...(cage?.depth_analytics || []).map((entry) => entry?.day),
        ...(cage?.alarms_data || []).map((entry) => entry?.day),
      ]).filter(Boolean),
    ),
  ).sort(dayOrder);
};

export const calculateDepthCoverage = (measured, noData) => {
  const measuredWindowCount = finiteNumber(measured) || 0;
  const expectedWindows = measuredWindowCount + (finiteNumber(noData) || 0);
  return expectedWindows > 0 ? (100 * measuredWindowCount) / expectedWindows : null;
};

/**
 * El winche es el equipo, no la cámara: una misma ruta puede llegar por varias cámaras y por
 * varias jaulas del grupo. Se conserva una sola copia —la de mayor cobertura— y nunca se suman
 * entre sí, porque serían el mismo minuto contado dos veces.
 */
const collectRoutes = (cages, selectedDays) => {
  const byIdentity = new Map();
  let duplicatesOmitted = 0;
  cages.forEach((cage) => {
    selectedEntryValues(cage?.depth_analytics, selectedDays).forEach((entry) => {
      routesFromValue(entry?.value).forEach((route) => {
        const identity = routeIdentity(entry?.day, route);
        const current = byIdentity.get(identity);
        if (!current) {
          byIdentity.set(identity, route);
          return;
        }
        duplicatesOmitted += 1;
        if (measuredWindows(route) > measuredWindows(current)) byIdentity.set(identity, route);
      });
    });
  });
  return { routes: Array.from(byIdentity.values()), duplicatesOmitted };
};

export const buildDepthAnalyticsView = ({
  jaula,
  jaulas,
  selectedDays = [],
  filterEnabled = true,
  threshold,
  defaultBinWidth = 1,
  defaultMinValid = 0.5,
  ladder = [],
} = {}) => {
  const cages = asCageList(jaula, jaulas);
  const { routes, duplicatesOmitted } = collectRoutes(cages, selectedDays);
  const contracts = new Set(
    routes.map((route) => `${route?.bin_width_m}|${route?.min_valid_m}|${route?.feeding_scope || 'center'}`),
  );
  const contractMismatch = contracts.size > 1;
  const firstRoute = routes[0] || {};
  const binWidth = finiteNumber(contractMismatch ? defaultBinWidth : firstRoute.bin_width_m)
    || finiteNumber(defaultBinWidth)
    || 1;
  const minValid = finiteNumber(contractMismatch ? defaultMinValid : firstRoute.min_valid_m)
    ?? finiteNumber(defaultMinValid)
    ?? 0.5;
  const requestedThreshold = finiteNumber(threshold) ?? minValid;
  const effectiveThreshold = !filterEnabled || requestedThreshold <= minValid
    ? requestedThreshold
    : Math.ceil((requestedThreshold - Number.EPSILON) / binWidth) * binWidth;

  const fedBins = {};
  const unfedBins = {};
  const summary = {
    measured: 0,
    noData: 0,
    transit: 0,
    repositions: 0,
    operatorRepositions: 0,
    routineRepositions: 0,
    minM: null,
    maxM: null,
    fedMinutes: 0,
    unfedMinutes: 0,
  };

  if (!contractMismatch) {
    routes.forEach((route) => {
      addBins(fedBins, route?.fed?.bins);
      addBins(unfedBins, route?.unfed?.bins);
      summary.measured += measuredWindows(route);
      summary.noData += finiteNumber(route?.sin_dato_winche) || 0;
      summary.transit += (finiteNumber(route?.fed?.en_transito) || 0) + (finiteNumber(route?.unfed?.en_transito) || 0);
      summary.repositions += finiteNumber(route?.reposicionamientos) || 0;
      summary.operatorRepositions += finiteNumber(route?.reposicionamientos_operador) || 0;
      summary.routineRepositions += finiteNumber(route?.reposicionamientos_rutina) || 0;
      const minimum = finiteNumber(route?.min_m);
      const maximum = finiteNumber(route?.max_m);
      if (minimum !== null) summary.minM = summary.minM === null ? minimum : Math.min(summary.minM, minimum);
      if (maximum !== null) summary.maxM = summary.maxM === null ? maximum : Math.max(summary.maxM, maximum);
    });
  }

  const alarmBins = {};
  let unknownAlarms = 0;
  let hiddenAlarms = 0;
  cages.forEach((cage) => {
    selectedEntryValues(cage?.alarms_data, selectedDays).forEach((entry) => {
      (Array.isArray(entry?.value) ? entry.value : []).forEach((alarm) => {
        const depth = finiteNumber(alarm?.snapshot?.depth ?? alarm?.depth);
        if (depth === null) {
          unknownAlarms += 1;
          return;
        }
        if (filterEnabled && depth < effectiveThreshold) {
          hiddenAlarms += 1;
          return;
        }
        const lower = Math.floor(depth / binWidth) * binWidth;
        const label = `${formatMetres(lower)}-${formatMetres(lower + binWidth)}`;
        alarmBins[label] = (alarmBins[label] || 0) + 1;
      });
    });
  });

  // Estratos medidos por el backend: perdidos contados por cámara y kg realmente entregados
  // mientras esa cámara estaba en la banda. Las bandas vienen por índice, no por etiqueta, y sólo
  // valen si el ancho con que se acumularon coincide con el que se está dibujando.
  const perdidosBins = {};
  const kgBins = {};
  const strataCoverage = { total: 0, sinDato: 0, sinAtribuir: 0, descartados: 0, mismatch: false, present: false };
  cages.forEach((cage) => {
    selectedEntryValues(cage?.depth_strata, selectedDays).forEach((entry) => {
      const strata = entry?.value;
      if (!strata || typeof strata !== 'object') return;
      if (finiteNumber(strata.bin_width_m) !== binWidth) {
        strataCoverage.mismatch = true;
        return;
      }
      strataCoverage.present = true;
      strataCoverage.total += finiteNumber(strata.total_s) || 0;
      strataCoverage.sinDato += finiteNumber(strata.sin_dato_s) || 0;
      strataCoverage.sinAtribuir += finiteNumber(strata.sin_atribuir_s) || 0;
      strataCoverage.descartados += finiteNumber(strata.descartados_s) || 0;
      Object.entries(strata.bins || {}).forEach(([band, bucket]) => {
        const index = finiteNumber(Number(band));
        if (index === null) return;
        // El umbral se aplica una sola vez, al recortar `visibleLabels`; filtrar acá además
        // sólo abriría la puerta a que ambos criterios se desincronicen.
        const lower = index * binWidth;
        const label = `${formatMetres(lower)}-${formatMetres(lower + binWidth)}`;
        perdidosBins[label] = (perdidosBins[label] || 0) + (finiteNumber(bucket?.perdidos) || 0);
        kgBins[label] = (kgBins[label] || 0) + (finiteNumber(bucket?.alimento_kg) || 0);
      });
    });
  });

  const labels = Array.from(
    new Set([...Object.keys(fedBins), ...Object.keys(unfedBins), ...Object.keys(alarmBins),
      ...Object.keys(perdidosBins)]),
  ).sort((left, right) => binBounds(left)[0] - binBounds(right)[0]);
  const thresholdIsSourceFloor = Math.abs(effectiveThreshold - minValid) < 1e-9;
  const visibleLabels = labels.filter((label) => {
    if (!filterEnabled || thresholdIsSourceFloor) return true;
    return binBounds(label)[0] >= effectiveThreshold;
  });

  // Cada lado se normaliza contra su propio total: comparar jaulas con jornadas distintas exige
  // porcentajes, no minutos absolutos.
  const visibleFedMinutes = visibleLabels.reduce((total, label) => total + (fedBins[label] || 0), 0);
  const visibleUnfedMinutes = visibleLabels.reduce((total, label) => total + (unfedBins[label] || 0), 0);
  summary.fedMinutes = sumCounts(fedBins);
  summary.unfedMinutes = sumCounts(unfedBins);
  const hiddenFedMinutes = summary.fedMinutes - visibleFedMinutes;
  const hiddenUnfedMinutes = summary.unfedMinutes - visibleUnfedMinutes;

  const rows = visibleLabels.map((label) => {
    const fed = contractMismatch ? null : fedBins[label] || 0;
    const unfed = contractMismatch ? null : unfedBins[label] || 0;
    const alarms = alarmBins[label] || 0;
    const fedPct = fed !== null && visibleFedMinutes > 0 ? (100 * fed) / visibleFedMinutes : null;
    const unfedPct = unfed !== null && visibleUnfedMinutes > 0 ? (100 * unfed) / visibleUnfedMinutes : null;
    // Tasa interna: ordena bandas con tiempos de exposición distintos. No se muestra —
    // normalizar por hora sólo confundía; lo que se lee en pantalla es el conteo crudo.
    const alarmsPerFed = fed !== null && fed > 0 ? alarms / fed : null;
    const [from, to] = binBounds(label);
    return {
      label,
      from,
      to,
      fed,
      unfed,
      alarms,
      fedPct,
      unfedPct,
      alarmsPerFed,
      perdidos: strataCoverage.present ? perdidosBins[label] || 0 : null,
      alimentoKg: strataCoverage.present ? kgBins[label] || 0 : null,
      lowSample: fedPct !== null && fedPct < DEPTH_LOW_SAMPLE_PCT,
    };
  });

  // Cada carril se pinta contra su propio máximo: son magnitudes distintas, ni comparables entre
  // sí ni entre jaulas. La leyenda imprime el máximo para que el color tenga referencia.
  const laneMax = {
    alarms: rows.reduce((top, row) => Math.max(top, row.alarms || 0), 0),
    perdidos: rows.reduce((top, row) => Math.max(top, row.perdidos || 0), 0),
    alimentoKg: rows.reduce((top, row) => Math.max(top, row.alimentoKg || 0), 0),
  };

  const visibleAlarms = rows.reduce((total, row) => total + row.alarms, 0);
  const averageAlarmsPerFed = visibleFedMinutes > 0 ? visibleAlarms / visibleFedMinutes : null;
  const highlight = rows
    .filter((row) => !row.lowSample && row.alarmsPerFed !== null && row.alarms > 0)
    .sort((left, right) => right.alarmsPerFed - left.alarmsPerFed)[0] || null;
  const highlightFactor = highlight && averageAlarmsPerFed
    ? highlight.alarmsPerFed / averageAlarmsPerFed
    : null;

  // Eje continuo: entre la banda mínima y la máxima ocupada no puede haber saltos invisibles, o
  // dos bandas distantes se leen como vecinas. Los huecos se dibujan; no se inventa dato.
  const axis = [];
  if (rows.length) {
    const indexOf = (row) => Math.round(row.from / binWidth);
    const byIndex = new Map(rows.map((row) => [indexOf(row), row]));
    const firstIndex = indexOf(rows[0]);
    const lastIndex = indexOf(rows[rows.length - 1]);
    let emptyRun = [];
    const flushRun = () => {
      if (!emptyRun.length) return;
      if (emptyRun.length * binWidth > DEPTH_GAP_CAP_M) {
        axis.push({
          kind: 'gap',
          bands: emptyRun.length,
          // Alto del corte: los metros que sí se dibujan. El rótulo dice los reales.
          cappedBands: Math.max(1, Math.round(DEPTH_GAP_CAP_M / binWidth)),
          metres: emptyRun.length * binWidth,
          from: emptyRun[0] * binWidth,
          to: (emptyRun[emptyRun.length - 1] + 1) * binWidth,
        });
      } else {
        emptyRun.forEach((index) => axis.push({
          kind: 'void',
          from: index * binWidth,
          to: (index + 1) * binWidth,
          label: `${formatMetres(index * binWidth)}-${formatMetres((index + 1) * binWidth)}`,
        }));
      }
      emptyRun = [];
    };
    for (let index = firstIndex; index <= lastIndex; index += 1) {
      const row = byIndex.get(index);
      if (row) {
        flushRun();
        axis.push({ kind: 'band', row });
      } else {
        emptyRun.push(index);
      }
    }
    flushRun();
  }

  // Con poca cobertura o poco tiempo medido, la banda ganadora no es un hallazgo: es ruido. La
  // vista publica la conclusión solo si se cumplen las cuatro condiciones, y si no dice qué falta.
  const coveragePct = calculateDepthCoverage(summary.measured, summary.noData);
  const confidenceReasons = [];
  if (coveragePct !== null && coveragePct < DEPTH_CONFIDENCE.minCoveragePct) {
    confidenceReasons.push({ code: 'coverage', percent: coveragePct });
  }
  if (!highlight) {
    confidenceReasons.push({ code: 'no_sample' });
  } else if (highlightFactor !== null && highlightFactor < DEPTH_CONFIDENCE.minFactor) {
    confidenceReasons.push({ code: 'no_standout', factor: highlightFactor });
  }

  // Peldaños de FeederScore sobre el mismo eje: el primero es la profundidad inicial esperada y
  // los siguientes son los objetivos tras cada alarma.
  const ladderStops = (Array.isArray(ladder) ? ladder : [])
    .map((value) => finiteNumber(value))
    .filter((value) => value !== null)
    .map((depthM, index) => {
      const row = rows.find((candidate) => depthM >= candidate.from && depthM < candidate.to) || null;
      return {
        depthM,
        role: index === 0 ? 'initial' : 'after_alarm',
        label: row?.label ?? null,
        alarms: row && !row.lowSample ? row.alarms : null,
      };
    });

  return {
    binWidth,
    minValid,
    effectiveThreshold,
    thresholdAdjusted: filterEnabled && Math.abs(requestedThreshold - effectiveThreshold) > 1e-9,
    contractMismatch,
    hasWindowData: routes.length > 0,
    routes: routes.map((route) => ({ winche_id: route?.winche_id, id_sensor: route?.id_sensor })),
    duplicatesOmitted,
    cageCount: cages.length,
    rows,
    axis,
    coveragePct,
    confidence: {
      publishable: confidenceReasons.length === 0 && Boolean(highlight),
      reasons: confidenceReasons,
    },
    highlightFactor,
    summary,
    visibleFedMinutes,
    visibleUnfedMinutes,
    hiddenFedMinutes,
    hiddenUnfedMinutes,
    visibleAlarms,
    averageAlarmsPerFed,
    laneMax,
    strataCoverage,
    highlight,
    ladderStops,
    unknownAlarms,
    hiddenAlarms,
  };
};
